import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../Context/Createcontext";
import wish from "../images/wish2.svg";
import DeletePrompt from "../Components/common/DeletePrompt";
import Popup from "../Components/common/Popup";
import { useNavigate } from "react-router-dom";
import addToCartIcon from "../Icons/addToCartIcon.svg";
import { useWishlist } from "../Context/WishlistContext";
import loader from "../images/loaderimg.png";
import HeroSection from "./HeroBannner";

/* ── inline keyframes ─────────────────────────────── */
const styles = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(28px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes shimmer {
    0%   { background-position: -200% 0; }
    100% { background-position:  200% 0; }
  }
  @keyframes heartPop {
    0%   { transform: scale(1);   }
    40%  { transform: scale(1.4); }
    100% { transform: scale(1);   }
  }
  @keyframes checkIn {
    0%   { transform: scale(.6); opacity: 0; }
    70%  { transform: scale(1.2);             }
    100% { transform: scale(1);  opacity: 1; }
  }
  @keyframes emptyFloat {
    0%,100% { transform: translateY(0px); }
    50%     { transform: translateY(-10px); }
  }

  .card-enter    { animation: fadeUp .45s cubic-bezier(.22,1,.36,1) both; }
  .heart-pop     { animation: heartPop .35s ease; }
  .check-in      { animation: checkIn .35s ease;  }
  .empty-float   { animation: emptyFloat 4s ease-in-out infinite; }

  .card-wrap:hover .img-zoom { transform: scale(1.07); }
  .img-zoom { transition: transform .5s cubic-bezier(.22,1,.36,1); }

  .delete-btn:hover svg { color: #ef4444; }
`;

/* ── Wishlist hero icon ── */
const WishlistHeroIcon = () => (
  <svg
    width="44"
    height="44"
    viewBox="0 0 24 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.386 0C4.743 0 3.1.644 1.859 1.942-.621 4.537-.618 8.697 1.859 11.294l9.468 9.929c.19.162.42.286.668.286s.473-.124.663-.286c3.158-3.305 6.318-6.614 9.477-9.919 2.48-2.596 2.48-6.757 0-9.353C19.655-.644 15.56-.644 13.08 1.95L12 3.076 10.913 1.942C9.673.644 8.03 0 6.386 0z"
      fill="#ff601e"
    />
  </svg>
);

/* ── Empty state illustration ── */
const EmptyIllustration = () => (
<div className="empty-float">
  <div
    className="w-36 h-36 rounded-[32px] flex items-center justify-center mx-auto"
    style={{
      background: "linear-gradient(135deg, #f0f4f4, #e8eded)",
      border: "2px dashed #a8e0db", // brighter border
    }}
  >
    <svg width="64" height="64" viewBox="0 0 24 22" fill="none">
      <path
        d="M6.386 0C4.743 0 3.1.644 1.859 1.942-.621 4.537-.618 8.697 1.859 11.294l9.468 9.929c.19.162.42.286.668.286s.473-.124.663-.286c3.158-3.305 6.318-6.614 9.477-9.919 2.48-2.596 2.48-6.757 0-9.353C19.655-.644 15.56-.644 13.08 1.95L12 3.076 10.913 1.942C9.673.644 8.03 0 6.386 0z"
        fill="#7fcac5" // darker heart
      />
    </svg>
  </div>
  </div>
);

const Wishlist = () => {
  const { handleAddToCart, showLoginPopup, setShowLoginPopup } = useCart();
  const {
    wishlist,
    loading,
    removeFromWishlist,
    fetchWishlist: refreshWishlist,
  } = useWishlist();
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addedIds, setAddedIds] = useState(new Set());
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    refreshWishlist();
  }, [refreshWishlist]);

  const goToProduct = (product_id, product) =>
    navigate(
      `/products/${product.pro.name?.toLowerCase().replace(/\s+/g, "-")}`,
      { state: { product_id } },
    );

  const outOfStock = (variants) => {
    if (Array.isArray(variants))
      return variants.length === 0 || variants.every((v) => v.quantity === 0);
    if (variants && typeof variants === "object")
      return variants.quantity === 0;
    return true;
  };

  const handleDeleteClick = (e, product_id, variant_id) => {
    e.stopPropagation();
    setSelectedProduct({ product_id, variant_id });
    setShowModal(true);
  };

  const handleAddWithFeedback = (e, product, variantId) => {
    e.preventDefault();
    const key = `${product.product_id}-${variantId}`;
    setAddedIds((prev) => new Set([...prev, key]));
    handleAddToCart({ ...product, quantity: 1 }, variantId);
    setTimeout(
      () =>
        setAddedIds((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        }),
      2000,
    );
  };

  return (
    <>
      <style>{styles}</style>

      <div
        className="min-h-screen font-poppins"
        style={{ background: "#f0f4f4" }}
      >
        {/* ── Hero ── */}
        <HeroSection
          breadcrumbs={[
            { label: "Home", to: "/home" },
            { label: "My Wishlist" },
          ]}
          title="My Wishlist"
          subtitle="Everything you love, saved in one place."
          badge="My Collection"
          icon={<WishlistHeroIcon />}
          stat={
            !loading && wishlist.length > 0
              ? {
                  value: wishlist.length,
                  label: `saved item${wishlist.length !== 1 ? "s" : ""}`,
                }
              : null
          }
          accentColor="#ff601e"
        />

        {/* ── Content ── */}
        <div className="px-5 sm:px-10 lg:px-16 pb-16 pt-8">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <img
                src={loader}
                alt="Loading…"
                className="w-14 h-14 animate-pulse"
              />
              <p className="text-[13px] text-[#8faba9] font-medium">
                Loading your wishlist…
              </p>
            </div>
          )}

          {/* Empty state */}
          {!loading && wishlist.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-6">
              <EmptyIllustration />
              <div className="text-center">
                <p
                  className="font-bold text-[22px] text-[#1e4a46] mb-2"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  Your wishlist is empty
                </p>
                <p className="text-[14px] text-[#8faba9] max-w-[280px] mx-auto leading-relaxed">
                  Browse our pharmacy collection and save the products you love.
                </p>
              </div>
              <Link
                to="/products"
                className="flex items-center gap-2 text-white font-semibold text-[14px] px-8 h-11 rounded-2xl transition-all duration-200 hover:-translate-y-0.5 mt-1"
                style={{
                  background: "linear-gradient(135deg,#162c2a,#1e4a46)",
                  boxShadow: "0 4px 18px rgba(22,44,42,.3)",
                }}
              >
                Browse Products
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          )}

          {/* Product grid */}
          {!loading && wishlist.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5">
              {wishlist.map((product, idx) => {
                const selectedVariant =
                  Array.isArray(product.variant) && product.variant.length > 0
                    ? product.variant[0]
                    : product.variant || {};

                const imageSrc =
                  product.pro?.product_images?.image_url?.primary_image ||
                  selectedVariant?.pro?.product_images?.image_url
                    ?.primary_image ||
                  "/default.svg";

                const price = selectedVariant?.price || "N/A";
                const discountedPrice = selectedVariant?.discounted_price;
                const oos = outOfStock(product.variant);
                const cardKey = `${product.product_id}-${product.variant_id}`;
                const isAdded = addedIds.has(cardKey);
                const multiVariant =
                  Array.isArray(product.variant) && product.variant.length > 1;
                const discount =
                  discountedPrice && discountedPrice < price
                    ? Math.round(((price - discountedPrice) / price) * 100)
                    : null;

                return (
                  <div
                    key={cardKey}
                    className="card-wrap card-enter relative flex flex-col rounded-[20px] overflow-hidden cursor-pointer"
                    style={{
                      animationDelay: `${idx * 55}ms`,
                      background: "#fff",
                      boxShadow:
                        hoveredCard === cardKey
                          ? "0 16px 48px rgba(22,44,42,.18), 0 2px 8px rgba(22,44,42,.08)"
                          : "0 2px 14px rgba(22,44,42,.08)",
                      transform:
                        hoveredCard === cardKey
                          ? "translateY(-6px)"
                          : "translateY(0)",
                      transition:
                        "box-shadow .3s ease, transform .3s cubic-bezier(.22,1,.36,1)",
                      border:
                        hoveredCard === cardKey
                          ? "1.5px solid #5fbdb3"
                          : "1.5px solid #e8eded",
                    }}
                    onMouseEnter={() => setHoveredCard(cardKey)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {/* Top accent bar on hover */}
                    <div
                      className="absolute top-0 left-0 right-0 h-[3px] rounded-t-[20px]"
                      style={{
                        background:
                          "linear-gradient(90deg,#162c2a,#5fbdb3,#ff601e)",
                        opacity: hoveredCard === cardKey ? 1 : 0,
                        transition: "opacity .3s ease",
                      }}
                    />

                    {/* Heart badge */}
                    <div className="absolute top-3 left-3 z-20">
                      <div
                        className="w-7 h-7 rounded-xl flex items-center justify-center"
                        style={{
                          background: "rgba(255,96,30,.1)",
                          border: "1px solid rgba(255,96,30,.2)",
                        }}
                      >
                        <img src={wish} alt="Saved" className="w-4 h-4" />
                      </div>
                    </div>

                    {/* Discount badge */}
                    {discount && (
                      <div className="absolute top-3 left-[42px] z-20">
                        <span
                          className="text-[9px] font-bold px-2 py-0.5 rounded-full"
                          style={{
                            background: "#eaf4f3",
                            color: "#1e4a46",
                            border: "1px solid #d4ecea",
                          }}
                        >
                          -{discount}%
                        </span>
                      </div>
                    )}

                    {/* Delete button */}
                    <button
                      className="delete-btn absolute top-3 right-3 z-20 w-7 h-7 rounded-xl flex items-center justify-center transition-all duration-200"
                      style={{
                        background: "rgba(255,255,255,.9)",
                        border: "1px solid #e8eded",
                      }}
                      onClick={(e) =>
                        handleDeleteClick(
                          e,
                          product.product_id,
                          product.variant_id,
                        )
                      }
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#8faba9"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2" />
                        <path d="M10 11v6M14 11v6" />
                        <path d="M5 6l1 14c.1 1.1.9 2 2 2h8c1.1 0 1.9-.9 2-2l1-14" />
                      </svg>
                    </button>

                    {/* Delete modal */}
                    {showModal && selectedProduct && (
                      <DeletePrompt
                        showModal={showModal}
                        onDelete={() => {
                          removeFromWishlist(
                            selectedProduct.product_id,
                            selectedProduct.variant_id,
                          );
                          setShowModal(false);
                        }}
                        onCancel={() => setShowModal(false)}
                      />
                    )}

                    {/* Image */}
                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault();
                        goToProduct(product.product_id, product);
                      }}
                      className="flex items-center justify-center overflow-hidden"
                      style={{
                        height: 164,
                        background: "#f8fafa",
                        borderBottom: "1px solid #f1f5f5",
                        paddingTop: 32,
                      }}
                    >
                      <img
                        src={imageSrc}
                        alt={product.pro?.name || "Product"}
                        className="img-zoom max-w-full object-contain"
                        style={{ maxHeight: 120 }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/default.svg";
                        }}
                      />
                    </Link>

                    {/* Content */}
                    <div className="flex flex-col gap-2 p-3.5 flex-1">
                      {/* Name */}
                      <h2
                        className="font-semibold text-[12px] sm:text-[13px] leading-snug line-clamp-2"
                        style={{ color: "#1e4a46", minHeight: "2.5em" }}
                      >
                        {product.pro?.name || "Unnamed Product"}
                      </h2>

                      {/* Price */}
                      <div className="flex items-center gap-2">
                        {discountedPrice && discountedPrice < price ? (
                          <>
                            <span
                              className="text-[10px] line-through"
                              style={{ color: "#8faba9" }}
                            >
                              ₹{price}
                            </span>
                            <span
                              className="font-bold text-[13px]"
                              style={{ color: "#162c2a" }}
                            >
                              ₹{discountedPrice}
                            </span>
                          </>
                        ) : (
                          <span
                            className="font-bold text-[13px]"
                            style={{ color: "#162c2a" }}
                          >
                            ₹{price}
                          </span>
                        )}
                      </div>

                      {/* OOS */}
                      {oos && (
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                          <span className="text-[10px] font-semibold text-red-500">
                            Out of Stock
                          </span>
                        </div>
                      )}

                      {/* Add to cart */}
                      <button
                        disabled={oos}
                        className="mt-auto w-full h-9 rounded-xl text-[11px] sm:text-[12px] font-semibold flex items-center justify-center gap-1.5 transition-all duration-200"
                        style={
                          oos
                            ? {
                                background: "#e2eaea",
                                color: "#8faba9",
                                cursor: "not-allowed",
                              }
                            : isAdded
                              ? {
                                  background: "#eaf4f3",
                                  color: "#1e4a46",
                                  border: "1px solid #d4ecea",
                                }
                              : {
                                  background:
                                    "linear-gradient(135deg,#162c2a,#1e4a46)",
                                  color: "#fff",
                                  boxShadow: "0 3px 10px rgba(22,44,42,.25)",
                                }
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          if (!localStorage.getItem("authToken")) {
                            setShowLoginPopup(true);
                            return;
                          }
                          if (multiVariant)
                            goToProduct(product.product_id, product);
                          else
                            handleAddWithFeedback(
                              e,
                              product,
                              product.variant_id,
                            );
                        }}
                      >
                        {isAdded ? (
                          <span className="check-in flex items-center gap-1">
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Added! ✓
                          </span>
                        ) : (
                          <>
                            <img
                              src={addToCartIcon}
                              alt="Cart"
                              className="w-3.5 h-3.5 object-contain"
                            />
                            {multiVariant ? "Choose Variant" : "Add to Cart"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showLoginPopup && <Popup onClose={() => setShowLoginPopup(false)} />}
    </>
  );
};

export default Wishlist;
